import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "../data/content";

const links = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/about", label: "About", testId: "nav-about" },
  { to: "/gallery", label: "Gallery", testId: "nav-gallery" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[var(--border-light)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <div
            className={`w-10 h-10 flex items-center justify-center transition-colors ${
              scrolled ? "bg-[var(--navy)]" : "bg-[var(--copper)]"
            }`}
          >
            <span className="font-serif-display text-white text-xl leading-none">A</span>
          </div>
          <div className="leading-tight">
            <div
              className={`font-serif-display text-lg ${
                scrolled ? "text-[var(--navy)]" : "text-white"
              }`}
            >
              Admiral Roofing
            </div>
            <div
              className={`text-[10px] tracking-[0.25em] uppercase ${
                scrolled ? "text-[var(--text-mute)]" : "text-white/70"
              }`}
            >
              & Home Improvement
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.22em] font-bold transition-colors ${
                  scrolled
                    ? isActive
                      ? "text-[var(--copper)]"
                      : "text-[var(--navy)] hover:text-[var(--copper)]"
                    : isActive
                      ? "text-[var(--copper)]"
                      : "text-white hover:text-[var(--copper)]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="nav-phone-link"
            className={`text-xs tracking-wider font-bold inline-flex items-center gap-2 ${
              scrolled ? "text-[var(--navy)]" : "text-white"
            }`}
          >
            <Phone size={14} />
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-estimate"
            className="btn-copper"
          >
            Free Estimate
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`md:hidden p-2 ${scrolled ? "text-[var(--navy)]" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-white border-t border-[var(--border-light)]"
        >
          <div className="container-x py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testId}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] font-bold ${
                    isActive ? "text-[var(--copper)]" : "text-[var(--navy)]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="m-nav-phone"
              className="text-sm font-bold text-[var(--navy)] inline-flex items-center gap-2"
            >
              <Phone size={14} /> {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              data-testid="m-nav-cta-estimate"
              className="btn-copper justify-center"
            >
              Free Estimate
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
